import { createEffect, createEvent, createStore } from 'effector';
import { pending } from 'patronum';
import { Chip, Link, Tooltip, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import i18n from '@shared/config/i18n';
import { createTableBag } from '@shared/tools/table';
import {
  DataGridColumns,
  DataGridRenderCellParams,
} from '@unicorn-shared/interface/uiKit/DataGrid/DataGrid.types';
import { path } from '../../page';
import { incidentsRelated } from '../api';
import * as Styled from '../styled';
import {
  GetIncidentsRelatedListPayload,
  GetIncidentsRelatedListResponse,
  RelatedDeletePayload,
  TableData,
} from '../types';

const { t } = i18n;

const statusChipColors: { [key: string]: 'success' | 'primary' | 'warning' } = {
  new: 'success',
  work: 'primary',
  confirmation: 'warning',
};

export const refreshList = createEvent();

export const fxGetList = createEffect<
  GetIncidentsRelatedListPayload,
  GetIncidentsRelatedListResponse,
  Error
>().use(incidentsRelated.getList);

export const $getListPending = pending({ effects: [fxGetList] });

export const relatedTicketsColumns: DataGridColumns<TableData> = [
  {
    field: 'number',
    headerName: '№',
    width: 100,
    sortable: false,
    hideable: false,
    renderCell: ({ value }: DataGridRenderCellParams<TableData, 'number'>) => (
      <Link href={`${path}/${value.id}`} target="_blank">
        {value.number}
      </Link>
    ),
  },
  {
    field: 'status',
    headerName: t('Label.status') ?? '',
    width: 100,
    sortable: false,
    hideable: false,
    renderCell: ({ value }: DataGridRenderCellParams<TableData, 'status'>) => (
      <Chip
        label={value.label}
        color={statusChipColors[value.id] ?? undefined}
        size="small"
      />
    ),
  },
  {
    field: 'type',
    headerName: t('RequestType') ?? '',
    width: 200,
    sortable: false,
    hideable: false,
    renderCell: ({ value }: DataGridRenderCellParams<TableData, 'type'>) => {
      if (!value.length) return null;

      return (
        <Styled.Types>
          {value.length > 1 && (
            <>
              <Tooltip title={value.map(({ title }) => title).join(' → ')}>
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </>
          )}

          <Typography variant="body2">{value[value.length - 1].title}</Typography>
        </Styled.Types>
      );
    },
  },
  {
    field: 'description',
    headerName: t('Description') ?? '',
    width: 200,
    sortable: false,
    hideable: false,
  },
  {
    field: 'objectTitle',
    headerName: t('AnObject') ?? '',
    width: 200,
    sortable: false,
    hideable: false,
  },
  {
    field: 'applicantFullname',
    headerName: t('Declarant') ?? '',
    width: 200,
    sortable: false,
    hideable: false,
  },
  {
    field: 'applicantPnone',
    headerName: t('DeclarantPhone') ?? '',
    width: 200,
    sortable: false,
    hideable: false,
  },
  {
    field: 'created_at',
    headerName: t('DateOfCreation') ?? '',
    width: 150,
    sortable: false,
    hideable: false,
  },
];

export const {
  $tableParams,
  $columns,
  columnsWidthChanged,
  orderColumnsChanged,
  $pagination,
  paginationModelChanged,
  $selectRow,
  selectionRowChanged,
  $visibilityColumns,
  visibilityColumnsChanged,
  $openRow,
  openRowChanged,
  $sorting,
  sortChanged,
  $pinnedColumns,
  changePinnedColumns,
  searchForm,
  mergeColumnsByPattern,
  $currentRow,
  $orderedColumns,
  $resizedColumns,
  setHiddenColumns,
  setPinnedColumns,
  setOrderedColumns,
  setResizedColumns,
  resetOrderedColumns,
  resetResizedColumns,
  setSorting,
  setPagination,
  $selectedRows,
  setSelectedRows,
} = createTableBag({
  columns: relatedTicketsColumns,
  paginationModel: {
    page: 0,
    pageSize: 10000,
  },
});

export const $tableData = createStore<TableData[]>([]);

export const startRelatedDelete = createEvent();
export const fxRelatedDelete = createEffect<RelatedDeletePayload, void, Error>().use(
  incidentsRelated.relatedDelete
);

export const resetSuccessRelatedDelete = createEvent();
export const $successRelatedDelete = createStore(false);
